import { styled } from "goober";
import { useEffect, useState } from "react";
import {
  useCodeEditorFilterQuery,
  useCodeEditorSearchContext,
} from "../CodeEditor/useCodeEditorSearch";
import { ScrollContainer, Table, Td, Th, Thead } from "../CompactTable";
import { Icon } from "../Icon";
import { Text } from "../Text";

const ExpandableTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  padding: 0;
  min-width: 100px;
  vertical-align: top;

  &[data-clickable]:hover {
    background: var(--alpha4);
    cursor: pointer;
  }

  &:last-child {
    border-right: none;
  }
`;

const ExpandableKeyTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  padding: 0;
  min-width: 100px;
  max-width: 320px;
  vertical-align: top;
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
  }

  &:last-child {
    border-right: none;
  }
`;

const ExpandToggle = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
`;

const Chevron = styled("span")`
  display: inline-flex;
  transition: transform 0.15s ease-out;

  &[data-expanded] {
    transform: rotate(90deg);
  }
`;

const NestedTable = styled("table")`
  width: 100%;
  border-collapse: collapse;
`;

const NestedTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  padding: 0 16px 2px;
  height: 36px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:first-child {
    width: 40%;
  }

  &:last-child {
    border-right: none;
  }

  tr:last-child > & {
    border-bottom: none;
  }
`;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function valueText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function rowMatches(
  row: Record<string, unknown>,
  columns: string[],
  query: string
): boolean {
  return columns.some((col) =>
    valueText(row[col]).toLowerCase().includes(query)
  );
}

function entryMatches([key, value]: [string, unknown], query: string): boolean {
  return (
    key.toLowerCase().includes(query) ||
    valueText(value).toLowerCase().includes(query)
  );
}

function useReportMatches(count: number) {
  const registration = useCodeEditorSearchContext();
  useEffect(() => {
    registration?.reportMatches(count);
  }, [registration, count]);
}

function ExpandableKeyValueRow({
  label,
  value,
  expanded,
  onToggle,
}: {
  label: string;
  value: Record<string, unknown>;
  expanded: boolean;
  onToggle: (e: React.MouseEvent) => void;
}) {
  const count = Object.keys(value).length;

  return (
    <tr>
      <ExpandableKeyTd onClick={onToggle}>
        <ExpandToggle>
          <Chevron data-expanded={expanded || undefined}>
            <Icon name="chevron-right" size={10} />
          </Chevron>
          <Text variant="code-regular">{label}</Text>
        </ExpandToggle>
      </ExpandableKeyTd>
      <ExpandableTd
        data-clickable={!expanded || undefined}
        onClick={!expanded ? onToggle : undefined}
      >
        {!expanded && (
          <ExpandToggle>
            <Text variant="code-regular" color="var(--text-secondary)">
              ({count})
            </Text>
          </ExpandToggle>
        )}
        {expanded && (
          <NestedTable>
            <tbody>
              {Object.entries(value).map(([k, v]) => (
                <tr key={k}>
                  <NestedTd>
                    <Text variant="code-regular" color="var(--text-secondary)">
                      {k}
                    </Text>
                  </NestedTd>
                  <NestedTd>
                    <CellValue value={v} />
                  </NestedTd>
                </tr>
              ))}
            </tbody>
          </NestedTable>
        )}
      </ExpandableTd>
    </tr>
  );
}

function CellValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return (
      <Text
        variant="code-regular"
        color="var(--text-tertiary)"
        style={{ fontStyle: "italic" }}
      >
        {value === null ? "null" : "—"}
      </Text>
    );
  }
  if (typeof value === "object") {
    const str = JSON.stringify(value);
    return (
      <Text variant="code-regular" color="var(--text-secondary)">
        {str.length > 80 ? `${str.slice(0, 80)}…` : str}
      </Text>
    );
  }
  return <Text variant="code-regular">{String(value)}</Text>;
}

type JsonShape =
  | {
      kind: "array-of-objects";
      data: Record<string, unknown>[];
      columns: string[];
    }
  | { kind: "single-object"; data: Record<string, unknown> }
  | { kind: "other" };

export type { JsonShape };

// Module-level cache so expanded state survives component remounts
// (e.g. when toggling the code panel restructures the React tree)
const expandedKeysCache = new Map<string, Set<string>>();

function getCacheKey(data: Record<string, unknown>): string {
  return Object.keys(data).sort().join(",");
}

function ArrayTable({
  columns,
  data,
  query,
}: {
  columns: string[];
  data: Record<string, unknown>[];
  query: string;
}) {
  const rows = query
    ? data.filter((row) => rowMatches(row, columns, query))
    : data;
  useReportMatches(rows.length);

  return (
    <ScrollContainer>
      <Table>
        <Thead>
          <tr>
            {columns.map((col) => (
              <Th key={col}>
                <Text variant="text-small" color="var(--text-secondary)">
                  {col}
                </Text>
              </Th>
            ))}
          </tr>
        </Thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // oxlint-disable-next-line react/no-array-index-key -- rows have no stable unique id
            <tr key={rowIndex}>
              {columns.map((col) => (
                <Td key={col}>
                  <CellValue value={row[col]} />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollContainer>
  );
}

function SingleObjectTable({
  data,
  query,
}: {
  data: Record<string, unknown>;
  query: string;
}) {
  const allEntries = Object.entries(data);
  const entries = query
    ? allEntries.filter((e) => entryMatches(e, query))
    : allEntries;
  useReportMatches(entries.length);
  const expandableKeys = new Set(
    entries.filter(([, v]) => isPlainObject(v)).map(([k]) => k)
  );
  const cacheKey = getCacheKey(data);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => expandedKeysCache.get(cacheKey) ?? new Set()
  );

  const updateExpandedKeys = (next: Set<string>) => {
    setExpandedKeys(next);
    expandedKeysCache.set(cacheKey, next);
  };

  const toggle = (key: string, e: React.MouseEvent) => {
    if (e.shiftKey) {
      const allExpanded = expandableKeys.size === expandedKeys.size;
      updateExpandedKeys(allExpanded ? new Set() : new Set(expandableKeys));
    } else {
      const next = new Set(expandedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      updateExpandedKeys(next);
    }
  };

  return (
    <ScrollContainer>
      <Table>
        <colgroup>
          <col style={{ width: "35%" }} />
          <col />
        </colgroup>
        <Thead>
          <tr>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Key
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Value
              </Text>
            </Th>
          </tr>
        </Thead>
        <tbody>
          {entries.map(([key, value]) =>
            isPlainObject(value) ? (
              <ExpandableKeyValueRow
                key={key}
                label={key}
                value={value}
                expanded={expandedKeys.has(key)}
                onToggle={(e) => toggle(key, e)}
              />
            ) : (
              <tr key={key}>
                <Td>
                  <Text variant="code-regular">{key}</Text>
                </Td>
                <Td>
                  <CellValue value={value} />
                </Td>
              </tr>
            )
          )}
        </tbody>
      </Table>
    </ScrollContainer>
  );
}

export function JsonTable({ shape }: { shape: JsonShape }) {
  const registration = useCodeEditorSearchContext();
  const query = useCodeEditorFilterQuery().trim().toLowerCase();

  useEffect(() => {
    registration?.registerFilter(true);
    return () => registration?.registerFilter(false);
  }, [registration]);

  if (shape.kind === "array-of-objects") {
    return (
      <ArrayTable columns={shape.columns} data={shape.data} query={query} />
    );
  }

  if (shape.kind === "single-object") {
    return <SingleObjectTable data={shape.data} query={query} />;
  }

  return null;
}
